/**
 * Scout MVP (spec V3) — asystent/opiekun małej firmy.
 * Ścieżka: dialog → wywiad → baza wiedzy → diagnoza → materiał do akceptacji → PDF / wysyłka / follow-up.
 * Menu 7 pozycji. Scenariusze (remonty, Allegro, oferta…) to kafelki i ścieżki rozmowy, nie moduły.
 */
import { useCallback, useEffect, useState } from 'react';
import { API } from '@/utils/api';
import ScoutFirma from '@/pages/ScoutFirma';
import ScoutDiagnoza from '@/pages/ScoutDiagnoza';
import ScoutPlan from '@/pages/ScoutPlan';
import ScoutZapytaj from '@/pages/ScoutZapytaj';
import ScoutSkrzynka from '@/pages/ScoutSkrzynka';
import ScoutLeadyCRM from '@/pages/ScoutLeadyCRM';
import ScoutOdpowiedzi from '@/pages/ScoutOdpowiedzi';
import ScoutUstawienia from '@/pages/ScoutUstawienia';
import ScoutRadarPulpit from '@/pages/ScoutRadarPulpit';
import {
  LayoutDashboard, Building2, Stethoscope, MessageCircleQuestion, Inbox, Users,
  Settings, Sun, Moon, ChevronRight, Plus, Loader2, AlertTriangle, Check, Pencil,
  ShieldCheck, Zap, LifeBuoy, Compass, Megaphone, FileText, MessageSquare,
  Package, BadgePercent, SearchCheck, Upload, ListChecks, Clock, ArrowUpRight, Menu, X, Radar,
} from 'lucide-react';

const AVATAR = 'https://static.prod-images.emergentagent.com/jobs/fd67d591-effc-4539-8480-d956258a4bbe/images/07dc99483d2306c7bdf51b057b3d13b23c763b2c65e402cee58218750ca06759.png';

const NAV = [
  { id: 'pulpit', label: 'Pulpit', desc: 'Najważniejsze rzeczy na dziś', icon: LayoutDashboard },
  { id: 'firma', label: 'O Twojej firmie', desc: 'Baza wiedzy Scouta', icon: Building2 },
  { id: 'diagnoza', label: 'Diagnoza', desc: 'Co masz, czego brakuje, co zrobić teraz', icon: Stethoscope },
  { id: 'zapytaj', label: 'Zapytaj Scouta', desc: 'Dialog, pytania, analiza materiałów', icon: MessageCircleQuestion },
  { id: 'skrzynka', label: 'Skrzynka Scouta', desc: 'Do akceptacji, do wysłania, historia, kosz', icon: Inbox },
  { id: 'leady', label: 'Leady i follow-upy', desc: 'Zapytania, kontakty i przypomnienia', icon: Users },
  { id: 'ustawienia', label: 'Ustawienia', desc: 'Dane firmy, styl, limity', icon: Settings },
];

const TILES = [
  { id: 'start', icon: Compass, label: 'Nie wiem, od czego zacząć', desc: 'Scout zada pytania i poprowadzi', opening: 'Nie wiem, od czego zacząć. Poprowadź mnie.' },
  { id: 'klienci', icon: Megaphone, label: 'Chcę więcej klientów', desc: 'Plan działań dopasowany do firmy', opening: 'Chcę zdobyć więcej klientów dla mojej firmy.' },
  { id: 'oferta', icon: FileText, label: 'Potrzebuję oferty', desc: 'Wywiad, oferta i PDF po akceptacji', opening: 'Potrzebuję przygotować ofertę dla klienta.' },
  { id: 'wiadomosc', icon: MessageSquare, label: 'Mam wiadomość od klienta', desc: 'Scout przygotuje odpowiedź', opening: 'Mam wiadomość od klienta i nie wiem, co odpisać.' },
  { id: 'produkt', icon: Package, label: 'Chcę sprzedać produkt', desc: 'Allegro, OLX i inne kanały', opening: 'Mam produkt i chcę go sprzedawać online.' },
  { id: 'reklama', icon: BadgePercent, label: 'Chcę reklamę', desc: 'Rozsądny budżet i teksty reklam', opening: 'Chcę zrobić reklamę i dobrze ulokować budżet.' },
  { id: 'braki', icon: SearchCheck, label: 'Sprawdź, czego mi brakuje', desc: 'Szybka diagnoza firmy', view: 'diagnoza' },
  { id: 'skaner-konkurencji', icon: Radar, label: 'Skaner konkurencji', desc: 'Sprawdź konkurencję i znajdź swoje przewagi', opening: 'Chcę sprawdzić konkurencję i znaleźć swoje przewagi. Podam teraz nazwę, link albo opis konkurenta (mogę też wgrać screen).' },
  { id: 'skaner-dokumentow', icon: Upload, label: 'Skaner dokumentów', desc: 'Wgraj PDF, screen, zdjęcie albo notatkę', opening: 'Chcę przeanalizować dokument lub materiał. Zaraz go wgram spinaczem albo wkleję treść.' },
];

const chip = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold';
const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';

const SCOUT_THEME_CSS = `
.scout-root {
  --sc-bg:#0C1526; --sc-topbar:rgba(12,21,38,.85); --sc-side:#0F1B31; --sc-input:#132039;
  --sc-t100:#ffffff; --sc-t90:rgba(255,255,255,.9); --sc-t85:rgba(255,255,255,.85); --sc-t80:rgba(255,255,255,.8);
  --sc-t70:rgba(255,255,255,.7); --sc-t60:rgba(255,255,255,.6); --sc-t55:rgba(255,255,255,.55); --sc-t50:rgba(255,255,255,.5);
  --sc-t45:rgba(255,255,255,.45); --sc-t40:rgba(255,255,255,.4); --sc-t35:rgba(255,255,255,.35); --sc-t30:rgba(255,255,255,.3);
  --sc-t25:rgba(255,255,255,.25); --sc-t15:rgba(255,255,255,.15);
  --sc-border:rgba(255,255,255,.1); --sc-border2:rgba(255,255,255,.15); --sc-border-soft:rgba(255,255,255,.05);
  --sc-surface:rgba(255,255,255,.04); --sc-surface2:rgba(255,255,255,.03); --sc-surface3:rgba(255,255,255,.06);
  --sc-surface4:rgba(255,255,255,.1); --sc-hover:rgba(255,255,255,.05); --sc-ring-track:rgba(255,255,255,.08);
  --sc-counter-grad:linear-gradient(135deg, rgba(242,106,33,.08), rgba(255,255,255,.03));
}
.scout-root[data-scout-theme="light"] {
  --sc-bg:#F2F5F9; --sc-topbar:rgba(242,245,249,.88); --sc-side:#FFFFFF; --sc-input:#FFFFFF;
  --sc-t100:#0B1B2B; --sc-t90:rgba(11,27,43,.92); --sc-t85:rgba(11,27,43,.88); --sc-t80:rgba(11,27,43,.84);
  --sc-t70:rgba(11,27,43,.76); --sc-t60:rgba(11,27,43,.68); --sc-t55:rgba(11,27,43,.62); --sc-t50:rgba(11,27,43,.56);
  --sc-t45:rgba(11,27,43,.52); --sc-t40:rgba(11,27,43,.48); --sc-t35:rgba(11,27,43,.44); --sc-t30:rgba(11,27,43,.38);
  --sc-t25:rgba(11,27,43,.32); --sc-t15:rgba(11,27,43,.2);
  --sc-border:rgba(11,27,43,.1); --sc-border2:rgba(11,27,43,.16); --sc-border-soft:rgba(11,27,43,.06);
  --sc-surface:#FFFFFF; --sc-surface2:rgba(11,27,43,.03); --sc-surface3:rgba(11,27,43,.06);
  --sc-surface4:rgba(11,27,43,.08); --sc-hover:rgba(11,27,43,.05); --sc-ring-track:rgba(11,27,43,.08);
  --sc-counter-grad:linear-gradient(135deg, rgba(242,106,33,.07), rgba(255,255,255,.65));
}
.scout-root [class*="bg-[#F26A21]"] { color:#fff !important; }
.scout-root[data-scout-theme="light"] .rounded-2xl.border { box-shadow: 0 1px 3px rgba(11,27,43,.05); }
.scout-root[data-scout-theme="light"] [class*="text-orange-2"],
.scout-root[data-scout-theme="light"] [class*="text-orange-3"] { color:#C2410C !important; }
`;

export default function ScoutStudio() {
  const [active, setActive] = useState('pulpit');
  const [leadyTab, setLeadyTab] = useState('leady');
  const [diagTab, setDiagTab] = useState('diagnoza');
  const [clients, setClients] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [clientId, setClientId] = useState(() => { try { return localStorage.getItem('scout_client_id') || ''; } catch { return ''; } });
  const [showAddModal, setShowAddModal] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [dialogOpening, setDialogOpening] = useState(null);
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem('scout_theme') || 'dark'; } catch { return 'dark'; } });

  const refreshClients = useCallback(async (selectId) => {
    setLoadError(false);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fetch(`${API}/scout/clients`);
        const data = await res.json();
        const list = data?.ok ? data.clients : [];
        setClients(list);
        if (selectId) setClientId(selectId);
        else setClientId((cur) => (list.some((c) => c.id === cur) ? cur : (list[0]?.id || '')));
        return;
      } catch {
        if (attempt < 2) await new Promise((r) => setTimeout(r, 2000));
      }
    }
    setClients([]);
    setLoadError(true);
  }, []);

  useEffect(() => { refreshClients(); }, [refreshClients]);
  useEffect(() => { try { if (clientId) localStorage.setItem('scout_client_id', clientId); } catch { /* noop */ } }, [clientId]);

  const client = (clients || []).find((c) => c.id === clientId) || (clients || [])[0];

  const toggleTheme = () => setTheme((t) => {
    const n = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem('scout_theme', n); } catch (e) { /* noop */ }
    return n;
  });
  const startDialog = (opening) => { setDialogOpening({ text: opening, ts: Date.now() }); setActive('zapytaj'); };
  const navigate = (view) => {
    if (view === 'plan') { setDiagTab('plan'); setActive('diagnoza'); return; }
    if (view === 'diagnoza') setDiagTab('diagnoza');
    setActive(view);
  };
  const onClientSaved = (updated) => setClients((list) => (list || []).map((c) => (c.id === updated.id ? updated : c)));

  const weakProfile = client && (client.completeness ?? 0) < 40;

  return (
    <div className="scout-root min-h-screen bg-[var(--sc-bg)] text-[var(--sc-t100)]" data-scout-theme={theme} data-testid="scout-studio" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{SCOUT_THEME_CSS}</style>
      <div className="flex">
        {/* SIDEBAR */}
        <aside className="hidden md:flex w-64 shrink-0 flex-col gap-5 border-r border-[var(--sc-border)] bg-[var(--sc-side)] px-4 py-6 min-h-screen sticky top-0 max-h-screen overflow-y-auto">
          <div className="flex items-center gap-3 px-2">
            <img src={AVATAR} alt="Scout" className="w-10 h-10 rounded-xl object-cover ring-1 ring-orange-400/40" />
            <div>
              <div className="text-lg font-extrabold tracking-tight leading-none">Scout</div>
              <div className="text-[10px] uppercase tracking-widest text-orange-300/80">opiekun Twojej firmy</div>
            </div>
          </div>

          <nav className="flex flex-col gap-1" data-testid="scout-nav">
            {NAV.map((n) => {
              const Icon = n.icon;
              const on = active === n.id;
              return (
                <button key={n.id} onClick={() => setActive(n.id)} data-testid={`nav-${n.id}`} title={n.desc}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${on ? 'bg-[#F26A21] text-white shadow-lg shadow-orange-900/40' : 'text-[var(--sc-t60)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)]'}`}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left leading-tight">{n.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="mt-auto">
            <div className={`${panel} p-3`}>
              <div className="flex items-center gap-2 text-sm font-semibold text-orange-300">
                <ShieldCheck className="w-4 h-4" /> Przygotowuję → Ty akceptujesz
              </div>
              <p className="text-[11px] text-[var(--sc-t40)] mt-1.5 leading-snug">Najpierw rozumiem, potem diagnozuję, potem przygotowuję konkret. Nic nie publikuję bez Twojej zgody.</p>
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="flex-1 min-w-0">
          {/* TOPBAR */}
          <div className="sticky top-0 z-10 flex items-center gap-2 sm:gap-3 border-b border-[var(--sc-border)] bg-[var(--sc-topbar)] backdrop-blur px-3 sm:px-5 py-3">
            <div className="md:hidden flex items-center gap-2 shrink-0">
              <button onClick={() => setMobileNav(true)} data-testid="mobile-nav-open" aria-label="Otwórz menu"
                className="w-9 h-9 rounded-xl border border-[var(--sc-border2)] flex items-center justify-center text-[var(--sc-t60)]">
                <Menu className="w-4 h-4" />
              </button>
              <img src={AVATAR} alt="Scout" className="w-8 h-8 rounded-lg" />
              <span className="font-extrabold hidden sm:inline">Scout</span>
            </div>
            <div className="flex items-center gap-2 ml-auto md:ml-0 min-w-0">
              <span className="text-xs text-[var(--sc-t40)] hidden sm:inline">Firma:</span>
              <div className="relative min-w-0">
                <select value={client?.id || ''} onChange={(e) => setClientId(e.target.value)} data-testid="client-switcher"
                  className="appearance-none rounded-xl border border-[var(--sc-border2)] bg-[var(--sc-input)] pl-3 pr-9 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400/50 cursor-pointer max-w-[120px] sm:max-w-[240px] truncate">
                  {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronRight className="w-4 h-4 rotate-90 absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--sc-t40)] pointer-events-none" />
              </div>
              <button onClick={() => setShowAddModal(true)} data-testid="add-client-btn"
                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--sc-border2)] px-3 py-2 text-xs font-semibold text-[var(--sc-t60)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)] transition shrink-0">
                <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Dodaj firmę</span>
              </button>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <button onClick={toggleTheme} data-testid="scout-theme-toggle" aria-label="Przełącz motyw"
                className="w-9 h-9 rounded-xl border border-[var(--sc-border2)] flex items-center justify-center text-[var(--sc-t60)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)] transition">
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="p-5 md:p-8 max-w-6xl">
            {clients === null && (
              <div className={`${panel} p-10 text-center`}><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#FF8A3D]" /><p className="text-sm text-[var(--sc-t45)] mt-3">Wczytuję dane firm…</p></div>
            )}
            {clients !== null && !client && loadError && (
              <div className={`${panel} p-10 text-center`} data-testid="clients-load-error">
                <AlertTriangle className="w-10 h-10 mx-auto text-[#FF8A3D] mb-3" />
                <p className="text-sm text-[var(--sc-t50)] mb-4">Nie udało się połączyć z serwerem — środowisko mogło się właśnie wybudzać. Odczekaj chwilę i spróbuj ponownie.</p>
                <button onClick={() => { setClients(null); refreshClients(); }} data-testid="clients-retry-btn"
                  className="rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-5 py-2.5 text-sm font-bold text-white transition">Spróbuj ponownie</button>
              </div>
            )}
            {clients !== null && !client && !loadError && (
              <div className={`${panel} p-10 text-center`} data-testid="no-clients">
                <Users className="w-10 h-10 mx-auto text-[var(--sc-t15)] mb-3" />
                <p className="text-sm text-[var(--sc-t50)] mb-4">Nie masz jeszcze żadnej firmy. Dodaj pierwszą, aby Scout mógł zacząć pracę.</p>
                <button onClick={() => setShowAddModal(true)} className="rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-5 py-2.5 text-sm font-bold text-white transition">Dodaj firmę</button>
              </div>
            )}
            {client && (
              <>
                {active === 'pulpit' && <Pulpit client={client} startDialog={startDialog} setActive={setActive} weakProfile={weakProfile} />}
                {active === 'firma' && <ScoutFirma client={client} onSaved={onClientSaved} onArchived={() => refreshClients()} onDone={() => setActive('diagnoza')} />}
                {active === 'diagnoza' && (
                  <TabbedView
                    tabs={[{ id: 'diagnoza', label: 'Diagnoza', icon: Stethoscope }, { id: 'plan', label: 'Plan działań', icon: ListChecks }]}
                    current={diagTab} onChange={setDiagTab} testid="diagnoza-tabs">
                    {(tab) => tab === 'diagnoza'
                      ? <ScoutDiagnoza client={client} onNavigate={navigate} />
                      : <ScoutPlan client={client} onNavigate={navigate} />}
                  </TabbedView>
                )}
                {active === 'zapytaj' && <ScoutZapytaj client={client} opening={dialogOpening} onNavigate={navigate} />}
                {active === 'skrzynka' && <ScoutSkrzynka client={client} onNavigate={setActive} />}
                {active === 'leady' && (
                  <TabbedView
                    tabs={[{ id: 'leady', label: 'Leady i follow-upy', icon: Users }, { id: 'odpowiedzi', label: 'Odpowiedź na wiadomość', icon: MessageSquare }]}
                    current={leadyTab} onChange={setLeadyTab} testid="leady-tabs">
                    {(tab) => tab === 'leady' ? <ScoutLeadyCRM client={client} /> : <ScoutOdpowiedzi client={client} />}
                  </TabbedView>
                )}
                {active === 'ustawienia' && <ScoutUstawienia client={client} theme={theme} onToggleTheme={toggleTheme} onNavigate={setActive} onArchived={() => refreshClients()} />}
              </>
            )}
          </div>
        </main>
      </div>

      {showAddModal && <AddClientModal onClose={() => setShowAddModal(false)} onCreated={(c) => { setShowAddModal(false); refreshClients(c.id); setActive('firma'); }} />}

      {/* MOBILE NAV DRAWER */}
      {mobileNav && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileNav(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[var(--sc-side)] border-r border-[var(--sc-border)] p-4 flex flex-col gap-4 overflow-y-auto">
            <div className="flex items-center gap-3">
              <img src={AVATAR} alt="Scout" className="w-9 h-9 rounded-xl object-cover ring-1 ring-orange-400/40" />
              <div className="flex-1">
                <div className="font-extrabold leading-none">Scout</div>
                <div className="text-[10px] uppercase tracking-widest text-orange-300/80">opiekun Twojej firmy</div>
              </div>
              <button onClick={() => setMobileNav(false)} data-testid="mobile-nav-close" aria-label="Zamknij menu"
                className="w-8 h-8 rounded-lg border border-[var(--sc-border2)] flex items-center justify-center text-[var(--sc-t50)]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => {
                const Icon = n.icon;
                const on = active === n.id;
                return (
                  <button key={n.id} onClick={() => { setActive(n.id); setMobileNav(false); }} data-testid={`mobile-nav-${n.id}`}
                    className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition ${on ? 'bg-[#F26A21] text-white' : 'text-[var(--sc-t60)] hover:bg-[var(--sc-hover)]'}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    <div className="flex-1 text-left">
                      <div className="leading-tight">{n.label}</div>
                      <div className={`text-[10px] mt-0.5 ${on ? 'text-white/70' : 'text-[var(--sc-t35)]'}`}>{n.desc}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Dodawanie firmy ---------- */
function AddClientModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const create = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setErr('');
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch(`${API}/scout/clients`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, industry }),
        });
        const data = await res.json();
        if (data?.ok && data.client) { onCreated(data.client); return; }
        setErr(data?.message || 'Nie udało się dodać firmy.');
        break;
      } catch {
        if (attempt === 0) { await new Promise((r) => setTimeout(r, 1500)); }
        else setErr('Błąd połączenia — serwer mógł się wybudzać. Spróbuj jeszcze raz.');
      }
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-client-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-[var(--sc-border2)] bg-[var(--sc-side)] p-6 space-y-4">
        <div>
          <div className="font-bold text-lg">Dodaj firmę</div>
          <p className="text-xs text-[var(--sc-t45)] mt-1">Po dodaniu przejdziesz do kreatora „O Twojej firmie", żeby Scout poznał firmę.</p>
        </div>
        <div>
          <div className="text-xs font-semibold text-[var(--sc-t55)] mb-1.5">Nazwa firmy</div>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()}
            data-testid="new-client-name"
            className="w-full rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50" />
        </div>
        <div>
          <div className="text-xs font-semibold text-[var(--sc-t55)] mb-1.5">Branża (opcjonalnie)</div>
          <input value={industry} onChange={(e) => setIndustry(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()}
            data-testid="new-client-industry" placeholder="np. remonty, salon beauty, sklep online"
            className="w-full rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50" />
        </div>
        {err && <p className="text-xs text-red-400" data-testid="new-client-error">{err}</p>}
        <div className="flex items-center gap-2 pt-1">
          <button onClick={create} disabled={saving || !name.trim()} data-testid="new-client-submit"
            className="inline-flex items-center gap-2 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-5 py-2.5 text-sm font-bold text-white transition disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Dodaj i uzupełnij profil
          </button>
          <button onClick={onClose} data-testid="new-client-cancel"
            className="rounded-xl border border-[var(--sc-border2)] px-4 py-2.5 text-sm font-semibold text-[var(--sc-t50)] hover:bg-[var(--sc-hover)] transition">Anuluj</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Zakładki wewnątrz widoku ---------- */
function TabbedView({ tabs, current, onChange, children, testid }) {
  const [inner, setInner] = useState(tabs[0].id);
  const cur = current ?? inner;
  const set = onChange ?? setInner;
  return (
    <div className="space-y-5">
      <div className="flex gap-2" data-testid={testid}>
        {tabs.map((t) => {
          const Icon = t.icon; const on = cur === t.id;
          return (
            <button key={t.id} onClick={() => set(t.id)} data-testid={`${testid}-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition border ${on ? 'bg-[#F26A21] border-transparent text-white' : 'border-[var(--sc-border2)] text-[var(--sc-t50)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)]'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {children(cur)}
    </div>
  );
}

/* ---------- PULPIT ---------- */
const TYPE_META = {
  oferta: { label: 'Oferta', color: '#F97316' },
  post: { label: 'Post', color: '#3B82F6' },
  ogloszenie: { label: 'Ogłoszenie', color: '#8B5CF6' },
  odpowiedz: { label: 'Odpowiedź', color: '#10B981' },
  email: { label: 'E-mail', color: '#06B6D4' },
  prosba_o_opinie: { label: 'Prośba o opinię', color: '#EAB308' },
  inne: { label: 'Materiał', color: '#9CA3AF' },
};

function Pulpit({ client, startDialog, setActive, weakProfile }) {
  const [inbox, setInbox] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    let live = true;
    fetch(`${API}/scout/inbox/${client.id}`).then((r) => r.json())
      .then((d) => { if (live && d?.ok) setInbox(d.items.filter((i) => i.status === 'do_akceptacji').slice(0, 3)); })
      .catch(() => { /* noop */ });
    fetch(`${API}/scout/leads/${client.id}`).then((r) => r.json())
      .then((d) => { if (live && d?.ok) setLeads(d.leads.filter((l) => l.followup_date && l.status !== 'zamkniete').slice(0, 3)); })
      .catch(() => { /* noop */ });
    return () => { live = false; };
  }, [client.id]);

  const accept = async (id) => {
    setInbox((list) => list.filter((i) => i.id !== id));
    try { await fetch(`${API}/scout/inbox/status`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, action: 'akceptuj' }) }); } catch { /* noop */ }
  };

  const pct = client.completeness ?? 0;
  return (
    <div className="space-y-8" data-testid="view-pulpit">
      {/* HERO */}
      <div className={`${panel} p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5`}>
        <img src={AVATAR} alt="Scout" className="w-16 h-16 rounded-2xl object-cover ring-2 ring-orange-400/40" />
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight">Cześć! W czym dziś pomóc firmie <span className="text-[#FF8A3D]">{client.name}</span>?</h1>
          <p className="text-[var(--sc-t50)] text-sm mt-1">Opisz sprawę własnymi słowami albo wybierz kafelek — poprowadzę Cię krok po kroku.</p>
        </div>
        <div className="min-w-[190px]" data-testid="brain-progress">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] text-[var(--sc-t50)]">Profil firmy</span>
            <span className="text-[11px] font-extrabold text-[#FF8A3D]">{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-[var(--sc-surface3)] overflow-hidden">
            <div className="h-full rounded-full bg-[#F26A21] transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <button onClick={() => setActive('firma')} data-testid="brain-fill-profile-btn"
            className="mt-2 text-[11px] font-bold text-orange-300 hover:underline inline-flex items-center gap-1">
            <Pencil className="w-3 h-3" /> Uzupełnij profil
          </button>
        </div>
      </div>

      {weakProfile && (
        <div className={`${panel} p-4 border-orange-400/30 flex flex-wrap items-center gap-3`} data-testid="weak-profile-banner">
          <AlertTriangle className="w-4 h-4 text-[#FF8A3D] shrink-0" />
          <p className="flex-1 text-sm text-[var(--sc-t70)]">Brakuje mi kilku informacji o firmie. Mogę pomagać ogólnie, ale lepszy efekt będzie po uzupełnieniu profilu.</p>
          <button onClick={() => setActive('firma')} data-testid="weak-profile-fill-btn"
            className="rounded-lg bg-[#F26A21] hover:bg-[#FF7C33] px-3.5 py-2 text-xs font-bold text-white transition">Uzupełnij profil</button>
        </div>
      )}

      {/* KAFELKI STARTOWE */}
      <section>
        <SectionTitle icon={Zap} title="Od czego zaczynamy?" hint="Kliknij kafelek — Scout zada kilka pytań i przygotuje konkret" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="start-tiles">
          {TILES.map((t) => (
            <button key={t.id} onClick={() => (t.view ? setActive(t.view) : startDialog(t.opening))} data-testid={`tile-${t.id}`}
              className={`${panel} p-4 text-left transition hover:border-orange-400/40 hover:bg-[var(--sc-surface3)] group`}>
              <div className="w-9 h-9 rounded-xl bg-orange-500/12 border border-orange-400/20 flex items-center justify-center mb-2.5">
                <t.icon className="w-4 h-4 text-[#FF8A3D]" />
              </div>
              <div className="text-sm font-bold leading-snug flex items-start gap-1">{t.label}
                <ArrowUpRight className="w-3.5 h-3.5 text-[var(--sc-t25)] group-hover:text-[#FF8A3D] transition shrink-0 mt-0.5" />
              </div>
              <div className="text-[11px] text-[var(--sc-t40)] mt-1 leading-snug">{t.desc}</div>
            </button>
          ))}
        </div>
      </section>

      {/* DO AKCEPTACJI (prawdziwe dane ze Skrzynki) */}
      <section>
        <SectionTitle icon={Inbox} title="Do akceptacji" hint="Scout przygotował — Ty decydujesz" />
        {inbox.length === 0 ? (
          <div className={`${panel} p-6 text-center text-sm text-[var(--sc-t45)]`} data-testid="pulpit-inbox-empty">
            Nic nie czeka na akceptację. Zacznij rozmowę ze Scoutem, a materiały pojawią się tutaj.
          </div>
        ) : (
          <div className="space-y-3" data-testid="pulpit-inbox-list">
            {inbox.map((it) => {
              const meta = TYPE_META[it.type] || TYPE_META.inne;
              return (
                <div key={it.id} className={`${panel} p-4 flex items-center gap-4`} data-testid={`pulpit-inbox-${it.id}`}>
                  <span className={chip} style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
                  <div className="flex-1 text-sm truncate">{it.title}</div>
                  <button onClick={() => accept(it.id)} data-testid={`pulpit-accept-${it.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#F26A21] hover:bg-[#FF7C33] px-3 py-2 text-sm font-semibold text-white transition">
                    <Check className="w-4 h-4" /> Akceptuj
                  </button>
                  <button onClick={() => setActive('skrzynka')} className="text-xs text-[var(--sc-t45)] hover:text-orange-300 transition" data-testid={`pulpit-open-inbox-${it.id}`}>Otwórz</button>
                </div>
              );
            })}
            <button onClick={() => setActive('skrzynka')} data-testid="pulpit-goto-inbox"
              className="text-xs font-semibold text-orange-300 hover:underline">Zobacz całą Skrzynkę Scouta →</button>
          </div>
        )}
      </section>

      {/* RADAR SCOUTA */}
      <ScoutRadarPulpit client={client} startDialog={startDialog} />

      {/* FOLLOW-UPY */}
      {leads.length > 0 && (
        <section>
          <SectionTitle icon={Clock} title="Najbliższe follow-upy" hint="Nie zgub zainteresowanego klienta" />
          <div className="space-y-2" data-testid="pulpit-followups">
            {leads.map((l) => (
              <div key={l.id} className={`${panel} p-3.5 flex items-center gap-3`}>
                <Clock className="w-4 h-4 text-[#FF8A3D] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{l.name}</div>
                  <div className="text-xs text-[var(--sc-t45)] truncate">{l.followup_date}{l.followup_note ? ` · ${l.followup_note}` : ''}</div>
                </div>
                <button onClick={() => setActive('leady')} className="text-xs text-orange-300 hover:underline shrink-0" data-testid={`pulpit-followup-${l.id}`}>Otwórz</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* OPIEKUN */}
      <div className={`${panel} p-5 flex flex-col sm:flex-row sm:items-center gap-4`} data-testid="pulpit-guardian-card">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-orange-500/15 border border-orange-400/25 flex items-center justify-center shrink-0">
            <LifeBuoy className="w-4 h-4 text-[#FF8A3D]" />
          </div>
          <div>
            <div className="text-sm font-bold">Potrzebujesz czegoś, czego Scout nie potrafi jeszcze zrobić?</div>
            <p className="text-xs text-[var(--sc-t50)] mt-0.5">Zadaj pytanie w rozmowie — jeśli wykracza poza moje możliwości, przekażę je opiekunowi APIS.</p>
          </div>
        </div>
        <button onClick={() => startDialog('Mam pytanie do opiekuna APIS.')} data-testid="pulpit-ask-guardian-btn"
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-orange-400/40 bg-orange-500/10 px-4 py-2.5 text-sm font-semibold text-orange-300 hover:bg-orange-500/20 transition shrink-0">
          Zapytaj Scouta
        </button>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, hint }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-bold flex items-center gap-2"><Icon className="w-5 h-5 text-[#FF8A3D]" /> {title}</h2>
      {hint && <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">{hint}</p>}
    </div>
  );
}

export { TYPE_META };
